import React, { useContext, useState } from 'react';
import { BoundarySign, ExactSign } from 'utils/filter/filter';
import {
    SupportedComparisonValue,
    TodayValue,
} from 'utils/components/FiltersModal/comparison/FilterComparison';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import classNames from 'classnames';
import useOnclickOutside from 'react-cool-onclickoutside';
import AppContext from 'app/AppContext';

import './DateControl.scss';
import {
    DateRangePickerCalendarValue,
    DateRangePickerCalendar,
    isDateRange,
    isPeriod,
    PeriodValue,
    periodValueDisplay,
} from '@instinet-analytics/semantic-ui-components';
import { dateRangeToString } from '@instinet-analytics/core-lib';

export type SupportedDateComparisonValue = string | string[] | PeriodValue;

interface DateControlProps {
    showSelectRange?: boolean;
    showToday?: boolean;
    sign: ExactSign | BoundarySign | undefined;
    value?: SupportedDateComparisonValue;
    onChange: (value: SupportedComparisonValue) => void;
}

enum Mode {
    ExactDate,
    Today,
    DateRange,
    Period,
}

export default function DateControl(props: DateControlProps) {
    const { value, showSelectRange, showToday, onChange } = props;

    const { timeFormat } = useContext(AppContext);
    const [isOpen, setIsOpen] = useState(false);

    const dayPickerRef = useOnclickOutside(() => {
        setIsOpen(false);
    });

    const toDate: (val?: string) => Date = (val) =>
        val ? new Date(parseInt(val)) : new Date();

    const mode: Mode = Array.isArray(value)
        ? Mode.DateRange
        : value === TodayValue
        ? Mode.Today
        : isPeriod(value)
        ? Mode.Period
        : Mode.ExactDate;

    let displayText = 'Pick a date';
    let dateValue: DateRangePickerCalendarValue | undefined;

    switch (mode) {
        case Mode.Today: {
            displayText = 'Today';
            dateValue = 'Today';
            break;
        }
        case Mode.Period: {
            const periodValue = value as PeriodValue;
            displayText = periodValueDisplay(periodValue);
            dateValue = periodValue;
            break;
        }
        case Mode.DateRange: {
            const valArr = value! as string[];
            const from = toDate(valArr[0]);
            const to = toDate(valArr[1]);
            displayText = dateRangeToString(timeFormat, from, to);
            dateValue = { from, to };
            break;
        }
        case Mode.ExactDate: {
            if (value !== undefined && value !== '') {
                dateValue = value ? toDate(value as string) : new Date();
                displayText = timeFormat.toDate(dateValue.getTime());
            }
        }
    }

    const onValueChange = (
        nextValue: DateRangePickerCalendarValue,
        keepOpen = false
    ) => {
        if (nextValue === 'Today') {
            !keepOpen && setIsOpen(false);
            onChange('Today');
            return;
        }

        if (isPeriod(nextValue)) {
            !keepOpen && setIsOpen(false);
            onChange(nextValue);
            return;
        }

        if (isDateRange(nextValue)) {
            onChange([
                nextValue.from.valueOf().toString(),
                nextValue.to.valueOf().toString(),
            ]);
            return;
        }

        onChange(nextValue.valueOf().toString());

        if (mode === Mode.DateRange) {
            // Don't close when changing from date range to date
            return;
        }

        !keepOpen && setIsOpen(false);
    };

    const displayCssClasses = classNames(
        'selected-date-display',
        value ? '' : 'none-selected'
    );

    return (
        <div className="DateControl">
            <div
                className={classNames(displayCssClasses)}
                onClick={() => setIsOpen(!isOpen)}
            >
                {displayText}
            </div>
            {isOpen && (
                <div className="date-selection-popup">
                    {isOpen && (
                        <div ref={dayPickerRef}>
                            <Icon
                                name="close"
                                onClick={() => setIsOpen(false)}
                            />

                            <DateRangePickerCalendar
                                value={dateValue}
                                onValueChange={onValueChange}
                                showSelectRange={showSelectRange}
                                showToday={showToday}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
